/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

/**
 * Represents a 2D point.
 */
export class Point2D {
  x!: number;
  y!: number;

  /**
   * @param x  the initial x coordinate of the point
   * @param y  the initial y coordinate of the point
   */
  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  getDistanceToPoint(p: Point2D): number {
    const dx = p.x - this.x;
    const dy = p.y - this.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * @param rect  a rectangle to compute a distance to
   * @returns a distance between this point and the provided rectangle, or 0 if this point is inside
   *          the rectangle or on its edge
   */
  getDistanceToRectangle(rect: Rectangle2D): number {
    const inXRange = this.x >= rect.getMinX() && this.x <= rect.getMaxX();
    const inYRange = this.y >= rect.getMinY() && this.y <= rect.getMaxY();
    if (inXRange && inYRange) {
      // a point is inside a rectangle or on it sedge
      return 0;
    }
    if (inXRange) {
      // the nearest distance is to a vertical rectangle's edge
      return Math.min(
          Math.abs(this.y - rect.getMinY()),
          Math.abs(this.y - rect.getMaxY())
      );
    }
    if (inYRange) {
      // the nearest distance is to a horizontal rectangle's edge
      return Math.min(
          Math.abs(this.x - rect.getMinX()),
          Math.abs(this.x - rect.getMaxX())
      );
    }
    // the nearest distance is to one of rectangle's corners
    const d1 = this.getDistanceToPoint(rect.getTopLeftPoint());
    const d2 = this.getDistanceToPoint(rect.getTopRightPoint());
    const d3 = this.getDistanceToPoint(rect.getBottomLeftPoint());
    const d4 = this.getDistanceToPoint(rect.getBottomRightPoint());
    return Math.min(d1, d2, d3, d4);
  }
}


/**
 * Represents a 2D segment.
 */
export class Segment2D {
  p1!: Point2D;
  p2!: Point2D;

  /**
   * @param {Point2D} p1  the first terminal point of the segment
   * @param {Point2D} p2  the second terminal point of the segment
   */
  constructor(p1: Point2D, p2: Point2D) {
    this.p1 = p1;
    this.p2 = p2;
  }

  getLength(): number {
    const dx = this.p2.x - this.p1.x;
    const dy = this.p2.y - this.p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Returns a point on a line where the segment lies by the specified `ratio` value.
   * A `ratio` between 0 and 1 returns a point that lies on the segment. A `ratio` of 0 corresponds
   * to `p1`, and 1 corresponds to `p2`.
   *
   * @param ratio  determines a position of the returned point that is determined as a linear
   *               interpolation between `p1` (when `ratio` is 0), and `p2` (when `ratio` is 1).
   *               A value outside of the [0..1] range will return a linearly extrapolated point on
   *               a line on which the segment lies, but outside of the segment
   * @returns A point that is a linear interpolation/extrapolation between the terminal
   *          segment's points
   */
  getColinearPoint(ratio: number): Point2D {
    const dx = this.p2.x - this.p1.x;
    const dy = this.p2.y - this.p1.y;
    return new Point2D(
        this.p1.x + dx * ratio,
        this.p1.y + dy * ratio);
  }
}


/**
 * Represents a 2D rectangle. The naming of the methods assumes that an x axis "grows" from left to
 * right, and "y" axis -- from top to bottom.
 */
export class Rectangle2D {
  x!: number;
  y!: number;
  width!: number;
  height!: number;

  /**
   * @param x      x coordinate of the top-left rectangle's corner
   * @param y      y coordinate of the top-left rectangle's corner
   * @param width   rectangle's width
   * @param height  rectangle's height
   */
  constructor(x: number, y: number, width: number, height: number) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }

  getMinX(): number {
    return Math.min(this.x, this.x + this.width);
  }

  getMaxX(): number {
    return Math.max(this.x, this.x + this.width);
  }

  getMinY(): number {
    return Math.min(this.y, this.y + this.height);
  }

  getMaxY(): number {
    return Math.max(this.y, this.y + this.height);
  }

  getTopLeftPoint(): Point2D {
    return new Point2D(this.getMinX(), this.getMinY());
  }

  getTopRightPoint(): Point2D {
    return new Point2D(this.getMaxX(), this.getMinY());
  }

  getBottomLeftPoint(): Point2D {
    return new Point2D(this.getMinX(), this.getMaxY());
  }

  getBottomRightPoint(): Point2D {
    return new Point2D(this.getMaxX(), this.getMaxY());
  }

  /**
   * @returns {Segment2D} the top edge of the rectangle
   */
  getTopEdge(): Segment2D {
    return new Segment2D(this.getTopLeftPoint(), this.getTopRightPoint());
  }

  /**
   * @returns {Segment2D} the bottom edge of the rectangle
   */
  getBottomEdge(): Segment2D {
    return new Segment2D(this.getBottomLeftPoint(), this.getBottomRightPoint());
  }

  /**
   * @returns {Segment2D} the left edge of the rectangle
   */
  getLeftEdge(): Segment2D {
    return new Segment2D(this.getTopLeftPoint(), this.getBottomLeftPoint());
  }

  /**
   * @returns {Segment2D} the right edge of the rectangle
   */
  getRightEdge(): Segment2D {
    return new Segment2D(this.getTopRightPoint(), this.getBottomRightPoint());
  }
}
